<template>
  <div class="register-page">
    <!-- Background decoration -->
    <div class="bg-decoration">
      <div class="circle circle-1"></div>
      <div class="circle circle-2"></div>
      <div class="circle circle-3"></div>
    </div>

    <!-- Main content -->
    <div class="register-content">
      <!-- Logo area -->
      <div class="logo-section">
        <lang-switcher class="lang-switcher-top" />
        <div class="logo-icon">🎉</div>
        <h1 class="app-name">{{ $t('registerPage.title') }}</h1>
        <p class="app-tagline">{{ $t('registerPage.tagline') }}</p>
      </div>

      <!-- Registration form -->
      <div class="register-form-container">
        <form class="register-form" @submit.prevent="handleRegister">
          <!-- Role selection -->
          <div class="section-title">
            <span class="section-number">0</span>
            <h3>{{ $t('registerPage.selectIdentity') }}</h3>
          </div>

          <!-- 档口客户注册（唯一选项） -->
          <div class="account-type-selector">
            <label class="account-type-option active-only">
              <span class="option-card">
                <span class="option-icon">🏪</span>
                <span class="option-title">{{ $t('registerPage.stallCustomer') }}</span>
                <span class="option-desc">{{ $t('registerPage.stallCustomerDesc') }}</span>
              </span>
            </label>
          </div>

          <!-- 隐藏角色字段 -->
          <input type="hidden" value="STALL" />

          <!-- Basic info section -->
          <div class="section-title">
            <span class="section-number">1</span>
            <h3>{{ $t('registerPage.basicInfo') }}</h3>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="username">
                <span class="icon">👤</span>
                {{ $t('registerPage.usernameLabel') }}
              </label>
              <input
                id="username"
                v-model="formData.username"
                type="text"
                :placeholder="$t('registerPage.usernamePlaceholder')"
                maxlength="20"
                required
              />
              <span class="help-text">{{ $t('registerPage.usernameHint') }}</span>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="email">
                <span class="icon">📧</span>
                {{ $t('registerPage.emailLabel') }}
              </label>
              <input
                id="email"
                v-model="formData.email"
                type="email"
                :placeholder="$t('registerPage.emailPlaceholder')"
                required
              />
            </div>
          </div>

          <!-- Password section -->
          <div class="section-title mt-2">
            <span class="section-number">2</span>
            <h3>{{ $t('registerPage.setPassword') }}</h3>
          </div>

          <div class="form-group">
            <label for="password">
              <span class="icon">🔒</span>
              {{ $t('registerPage.passwordLabel') }}
            </label>
            <div class="password-input-wrapper">
              <input
                id="password"
                v-model="formData.password"
                :type="showPassword ? 'text' : 'password'"
                :placeholder="$t('registerPage.passwordPlaceholder')"
                minlength="6"
                required
              />
              <button 
                type="button" 
                class="password-toggle"
                @click="showPassword = !showPassword"
              >
                {{ showPassword ? '🙈' : '👁️' }}
              </button>
            </div>
            
            <!-- Password strength indicator -->
            <div class="password-strength">
              <div class="strength-bar" :class="passwordStrength"></div>
              <span class="strength-text">{{ strengthText }}</span>
            </div>
          </div>

          <div class="form-group">
            <label for="confirmPassword">
              <span class="icon">🔐</span>
              {{ $t('registerPage.confirmPasswordLabel') }}
            </label>
            <div class="password-input-wrapper">
              <input
                id="confirmPassword"
                v-model="formData.confirmPassword"
                :type="showConfirmPassword ? 'text' : 'password'"
                :placeholder="$t('registerPage.confirmPasswordPlaceholder')"
                required
              />
              <button 
                type="button" 
                class="password-toggle"
                @click="showConfirmPassword = !showConfirmPassword"
              >
                {{ showConfirmPassword ? '🙈' : '👁️' }}
              </button>
            </div>
          </div>

          <!-- Shop info section -->
          <div class="section-title mt-2">
            <span class="section-number">3</span>
            <h3>{{ $t('registerPage.shopInfo') }}</h3>
          </div>

          <div class="form-group">
            <label for="customerName">
              <span class="icon">🏪</span>
              {{ $t('registerPage.shopNameLabel') }}
            </label>
            <input
              id="customerName"
              v-model="formData.customerName"
              type="text"
              :placeholder="$t('registerPage.shopNamePlaceholder')"
              maxlength="50"
              required
            />
            <span class="help-text">{{ $t('registerPage.shopNameHint') }}</span>
          </div>

          <div class="form-group" v-if="accountType === 'company'">
            <label for="shopLicense">
              <span class="icon">📄</span>
              {{ $t('registerPage.licenseLabel') }}
            </label>
            <input
              id="shopLicense"
              v-model="formData.shopLicense"
              type="text"
              :placeholder="$t('registerPage.licensePlaceholder')"
            />
          </div>

          <!-- Terms agreement -->
          <div class="section-title mt-2">
            <span class="section-number">4</span>
            <h3>{{ $t('registerPage.agreeTerms') }}</h3>
          </div>

          <div class="terms-agreement touch-feedback">
            <label class="checkbox-label">
              <input 
                type="checkbox" 
                v-model="agreeTerms" 
                required
              />
              <span class="custom-checkbox"></span>
              <span class="terms-text">{{ $t('registerPage.agreeTerms') }}</span>
            </label>
            <div class="terms-links">
              <a href="#" @click.prevent="showTerms('service')">{{ $t('registerPage.termsOfService') }}</a>
              <a href="#" @click.prevent="showTerms('privacy')">{{ $t('registerPage.privacyPolicy') }}</a>
              <a href="#" @click.prevent="showTerms('user')">{{ $t('registerPage.userAgreement') }}</a>
            </div>
          </div>

          <!-- Submit button -->
          <button 
            type="submit" 
            class="btn-submit touch-feedback"
            :class="{ loading: loading }"
            :disabled="loading"
          >
            <span v-if="!loading">{{ $t('registerPage.registerBtn') }}</span>
            <span v-else>
              <span class="spinner"></span>
              {{ $t('registerPage.registerLoading') }}
            </span>
          </button>

          <!-- Login link -->
          <div class="login-link">
            <span>{{ $t('registerPage.hasAccount') }}</span>
            <router-link :to="loginLink" class="login-link-text">{{ $t('registerPage.loginNow') }}</router-link>
          </div>

          <!-- App Store 下载链接 -->
          <div class="app-download-link">
            <span>📱</span>
            <a href="https://apps.apple.com/cn/app/wholesale/id6809011555" target="_blank" class="app-link">下载 APP 体验更佳</a>
          </div>
        </form>
      </div>
    </div>

    <!-- Footer -->
    <footer class="register-footer">
      <router-link to="/" class="back-home-link">← {{ $t('registerPage.backToHome') || '返回首页' }}</router-link>
    </footer>

    <!-- Toast notification -->
    <div v-if="toast.show" :class="['toast', `toast-${toast.type}`, { show: toast.show }]">
      {{ toast.message }}
    </div>

    <!-- Terms modal -->
    <div v-if="showTermsModal" class="modal-overlay" @click.self="showTermsModal = false">
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ currentTermsTitle }}</h3>
          <button class="close-btn" @click="showTermsModal = false">×</button>
        </div>
        <div class="modal-body">
          <div v-html="currentTermsContent"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';
import LangSwitcher from '@/components/LangSwitcher.vue';

export default {
  name: 'Register',
  components: { LangSwitcher },
  data() {
    return {
      selectedRole: 'STALL', // 默认档口客户
      accountType: 'individual', // ⭐ 企业类型
      formData: {
        username: '',
        email: '',
        phone: '',
        emailCode: '',
        password: '',
        confirmPassword: '',
        customerName: '',
        shopLicense: ''
      },
      showPassword: false,
      showConfirmPassword: false,
      agreeTerms: false,
      loading: false,
      toast: {
        show: false,
        message: '',
        type: 'info'
      },
      showTermsModal: false,
      currentTermsType: 'service',
      passwordStrength: 'weak',
      termsContent: {
        service: {
          title: '服务条款',
          content: `
            <h4>欢迎使用 Naporlun 批发系统</h4>
            <p>请您仔细阅读本服务条款，使用本系统即表示您同意遵守以下条款：</p>
            <h5>1. 服务说明</h5>
            <p>Naporlun 批发系统为批发商和档口客户提供商品管理、订单处理、库存管理等服务。</p>
            <h5>2. 账号责任</h5>
            <p>• 您应对账号下的所有活动负责<br/>• 不得将账号转让、出借给他人<br/>• 发现账号被盗用应立即通知我们</p>
            <h5>3. 使用规范</h5>
            <p>• 不得利用系统从事违法活动<br/>• 不得干扰或破坏系统正常运行<br/>• 不得未经授权访问他人数据</p>
            <h5>4. 免责声明</h5>
            <p>因不可抗力、系统维护或网络故障导致的服务中断，我们不承担责任。</p>
          `
        },
        privacy: {
          title: '隐私政策',
          content: '<h4>我们如何收集和使用您的信息</h4><h5>1. 信息收集</h5><p>我们会收集以下信息：</p><p>注册信息：用户名、邮箱、店铺名称</p><p>交易信息：订单记录、支付信息</p><p>设备信息：设备型号、操作系统、IP地址</p><p>使用信息：登录时间、操作记录</p><h5>2. 信息使用</h5><p>您的信息用于：</p><p>提供和改进服务、处理订单和支付、发送服务通知、保障账号安全</p><h5>3. 信息保护</h5><p>采用加密技术保护数据传输，严格限制员工访问权限，定期安全审计，不会向第三方出售您的信息</p><h5>4. 您的权利</h5><p>查看和修改个人信息、删除账号及相关数据、撤回授权同意</p>'
        },
        user: {
          title: '用户协议',
          content: `
            <h4>用户权利与义务</h4>
            <h5>1. 用户权利</h5>
            <p>• 使用系统提供的各项服务<br/>• 查询自己的订单和交易记录<br/>• 获得技术支持和客户服务<br/>• 对服务提出建议和投诉</p>
            <h5>2. 用户义务</h5>
            <p>• 提供真实、准确的注册信息<br/>• 及时更新信息以保持准确<br/>• 妥善保管账号密码<br/>• 遵守相关法律法规</p>
            <h5>3. 交易规则</h5>
            <p>• 批发商应如实描述商品信息<br/>• 档口客户应按时支付货款<br/>• 双方应诚信交易，不得欺诈<br/>• 争议应友好协商或申请平台介入</p>
            <h5>4. 知识产权</h5>
            <p>系统内的所有内容（包括但不限于文字、图片、软件）均受知识产权法保护，未经授权不得复制或使用。</p>
            <h5>5. 协议修改</h5>
            <p>我们保留修改本协议的权利，修改后的协议将在系统内公布，继续使用即视为同意。</p>
          `
        }
      }
    };
  },
  computed: {
    // 登录链接携带批发商ID
    loginLink() {
      const wholesalerId = this.$route.query.wholesalerId || this.$route.params.wholesalerId;
      return wholesalerId ? `/login?wholesalerId=${wholesalerId}` : '/login';
    },
    strengthText() {
      return this.$t(`registerPage.password${this.passwordStrength.charAt(0).toUpperCase() + this.passwordStrength.slice(1)}`);
    },
    currentTermsTitle() {
      return this.termsContent[this.currentTermsType]?.title || ''
    },
    currentTermsContent() {
      return this.termsContent[this.currentTermsType]?.content || ''
    }
  },
  methods: {
    /**
     * 从路由参数获取批发商ID
     * 支持 query 参数 (?wholesalerId=8) 和路由参数 (/register/:wholesalerId)
     */
    getWholesalerId() {
      return this.$route.query.wholesalerId || this.$route.params.wholesalerId || null;
    },
    async handleRegister() {
      // Validation
      if (!this.formData.username || !this.formData.email) {
        this.showToast(this.$t('registerPage.fillAllRequired') || '请填写必填项', 'error');
        return;
      }

      if (!this.agreeTerms) {
        this.showToast(this.$t('registerPage.agreeTermsHint'), 'error');
        return;
      }

      if (this.formData.password !== this.formData.confirmPassword) {
        this.showToast(this.$t('registerPage.passwordMismatch'), 'error');
        return;
      }

      if (this.formData.password.length < 6) {
        this.showToast(this.$t('registerPage.passwordTooShort'), 'error');
        return;
      }

      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.formData.email)) {
        this.showToast(this.$t('registerPage.invalidEmail'), 'error');
        return;
      }

      this.loading = true;
      try {
        // 调用注册 API
        const shareCode = this.$route.query.shareCode || '';
        const stallId = this.$route.query.stallId || null;
        
        // ⭐ 检测批发商ID：如果从批发商店铺页面注册，自动关联批发商
        let wholesalerId = null;
        const wholesalerIdFromRoute = this.getWholesalerId();
        if (wholesalerIdFromRoute) {
          try {
            const wholesalerInfo = await api.get(`/public/wholesale/${wholesalerIdFromRoute}`);
            if (wholesalerInfo && wholesalerInfo.id) {
              wholesalerId = wholesalerInfo.id;
              console.log('批发商ID关联:', wholesalerIdFromRoute, '→', wholesalerId);
            }
          } catch (e) {
            console.warn('获取批发商信息失败:', e);
          }
        }
        
        const response = await api.post('/auth/register', {
          username: this.formData.username,
          password: this.formData.password,
          confirmPassword: this.formData.confirmPassword,
          customerName: this.formData.customerName,
          email: this.formData.email,
          phone: this.formData.phone,
          roleCode: this.selectedRole, // 选择的角色
          shareCode: shareCode || undefined,
          stallId: stallId ? parseInt(stallId) : undefined,
          wholesalerId: wholesalerId || undefined
        });

        if (response && response.success) {
          this.showToast('注册成功，请登录', 'success');
          
          // 注册成功后跳转登录页，清除分享码（已关联完成）
          setTimeout(() => {
            this.$router.push('/login')
          }, 1500)
        } else {
          throw new Error((response && response.message) || this.$t('registerPage.registerFailed'));
        }
      } catch (error) {
        console.error('注册失败:', error);
        const errorMsg = error.response?.data?.message || error.message || this.$t('registerPage.registerFailed');
        this.showToast(errorMsg, 'error');
      } finally {
        this.loading = false;
      }
    },

    showToast(message, type = 'info') {
      this.toast.message = message;
      this.toast.type = type;
      this.toast.show = true;

      setTimeout(() => {
        this.toast.show = false;
      }, 3000);
    },

    showTerms(type) {
      this.currentTermsType = type;
      this.showTermsModal = true;
    },

    calculatePasswordStrength(password) {
      let strength = 0;
      
      if (password.length >= 6) strength++;
      if (password.length >= 8) strength++;
      if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
      if (/[0-9]/.test(password)) strength++;
      if (/[^a-zA-Z0-9]/.test(password)) strength++;

      if (strength <= 2) return 'weak';
      if (strength <= 4) return 'medium';
      return 'strong';
    }
  },
  mounted() {
    // Check if already logged in
    if (localStorage.getItem('token') || sessionStorage.getItem('token')) {
      this.$router.push('/dashboard');
      return;
    }
  },
  watch: {
    'formData.password': function(newVal) {
      this.passwordStrength = this.calculatePasswordStrength(newVal);
    }
  }
};
</script>

<style lang="scss" scoped>
.register-page {
  min-height: 100vh;
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

// Background decorations
.bg-decoration {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  pointer-events: none;
  
  .circle {
    position: absolute;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.1);
    animation: float 20s infinite ease-in-out;
  }
  
  .circle-1 { width: 200px; height: 200px; top: -50px; left: -50px; animation-delay: 0s; }
  .circle-2 { width: 150px; height: 150px; bottom: 100px; right: -30px; animation-delay: -5s; }
  .circle-3 { width: 100px; height: 100px; top: 50%; right: 20%; animation-delay: -10s; }
}

@keyframes float {
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-30px) rotate(180deg); }
}

// Main content
.register-content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 30px 20px;
  max-width: 480px;
  margin: 0 auto;
}

// Logo section
.logo-section {
  text-align: center;
  margin-bottom: 30px;
  position: relative;
  
  .lang-switcher-top {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 10;
  }
  
  .logo-icon {
    font-size: 56px;
    margin-bottom: 12px;
    animation: bounce 2s infinite;
  }
  
  .app-name {
    font-size: 26px;
    font-weight: bold;
    color: white;
    margin: 0 0 6px 0;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
  
  .app-tagline {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.9);
    margin: 0;
  }
}

@keyframes bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}

// Form container
.register-form-container {
  background: white;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

// Account type selector
.account-type-selector {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  margin-bottom: 24px;
  
  .account-type-option.active-only .option-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 16px 12px;
    border: 2px solid #667eea;
    border-radius: 12px;
    background: linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1));
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
    cursor: default;
    
    .option-icon {
      font-size: 32px;
    }
    
    .option-title {
      font-size: 14px;
      font-weight: 600;
      color: #667eea;
    }
    
    .option-desc {
      font-size: 11px;
      color: #999;
    }
  }
}

// Section title
.section-title {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 24px 0 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid #f0f0f0;
  
  .section-number {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-radius: 50%;
    font-size: 12px;
    font-weight: bold;
    flex-shrink: 0;
  }
  
  h3 {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    color: #2c3e50;
  }
}

.mt-2 { margin-top: 24px; }

// Form row
.form-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  
  @media (max-width: 400px) {
    grid-template-columns: 1fr;
  }
}

.form-group {
  grid-column: span 2;
  margin-bottom: 16px;
  
  label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 500;
    color: #2c3e50;
    margin-bottom: 6px;
    
    .icon {
      font-size: 14px;
    }
  }
  
  input {
    width: 100%;
    padding: 12px 14px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    font-size: 14px;
    transition: all 0.3s ease;
    outline: none;
    background: #fafafa;
    
    &:focus {
      border-color: #667eea;
      background: white;
      box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
    }
    
    &::placeholder {
      color: #aaa;
    }
  }
  
  .help-text {
    font-size: 11px;
    color: #999;
    margin-top: 4px;
  }
}

// Phone input wrapper
.phone-input-wrapper {
  display: flex;
  gap: 8px;
  
  input {
    flex: 1;
  }
  
  .sms-code-btn {
    padding: 12px 16px;
    background: #667eea;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 13px;
    cursor: pointer;
    white-space: nowrap;
    transition: all 0.2s ease;
    
    &:hover:not(:disabled) {
      background: #5568d3;
    }
    
    &:disabled {
      background: #ccc;
      cursor: not-allowed;
    }
  }
}

// Password wrapper
.password-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  
  input {
    flex: 1;
    padding-right: 44px;
  }
  
  .password-toggle {
    position: absolute;
    right: 8px;
    background: none;
    border: none;
    font-size: 16px;
    cursor: pointer;
    opacity: 0.7;
    
    &:hover {
      opacity: 1;
    }
  }
}

// Password strength
.password-strength {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  
  .strength-bar {
    width: 80px;
    height: 4px;
    background: #eee;
    border-radius: 2px;
    overflow: hidden;
    
    &::after {
      content: '';
      display: block;
      height: 100%;
      width: 33%;
      background: #e74c3c;
      border-radius: 2px;
      transition: all 0.3s ease;
    }
    
    &.medium::after {
      width: 66%;
      background: #f39c12;
    }
    
    &.strong::after {
      width: 100%;
      background: #2ecc71;
    }
  }
  
  .strength-text {
    font-size: 11px;
    color: #e74c3c;
  }
}

// Terms agreement
.terms-agreement {
  .checkbox-label {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 12px;
    color: #666;
    cursor: pointer;
    
    input[type='checkbox'] {
      width: 16px;
      height: 16px;
      margin-top: 2px;
      cursor: pointer;
      accent-color: #667eea;
    }
    
    .terms-text {
      line-height: 1.4;
    }
  }
  
  .terms-links {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 12px;
    padding: 12px;
    background: #f8f9ff;
    border-radius: 8px;
    border: 1px solid #e0e6ff;
    
    a {
      font-size: 14px;
      color: #667eea;
      text-decoration: none;
      cursor: pointer;
      font-weight: 500;
      padding: 8px 16px;
      border-radius: 4px;
      transition: all 0.2s;
      
      &:hover {
        color: #5568d3;
        background: rgba(102, 126, 234, 0.1);
      }
      
      &::before {
        content: '📄 ';
        margin-right: 4px;
      }
    }
  }
}

// Submit button
.btn-submit {
  width: 100%;
  padding: 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 24px;
  margin-bottom: 16px;
  
  &:active:not(:disabled) {
    transform: scale(0.98);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
  
  .spinner {
    width: 20px;
    height: 20px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    border-top-color: white;
    animation: spin 0.8s linear infinite;
  }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

// Links
.login-link {
  text-align: center;
  font-size: 14px;
  color: #666;
  
  .login-link-text {
    color: #667eea;
    font-weight: 600;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
}

// App Store 下载链接
.app-download-link {
  text-align: center;
  font-size: 13px;
  color: #999;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
  
  span {
    margin-right: 4px;
  }
  
  .app-link {
    color: #667eea;
    text-decoration: none;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
}

// Footer
.register-footer {
  position: absolute;
  bottom: 16px;
  left: 0;
  right: 0;
  text-align: center;
  
  p {
    margin: 0;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.8);
    
    a {
      color: rgba(255, 255, 255, 0.9);
      text-decoration: none;
      
      &:hover {
        text-decoration: underline;
      }
    }
  }
}

// Toast
.toast {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%) translateY(-100px);
  padding: 14px 24px;
  border-radius: 10px;
  color: white;
  font-size: 14px;
  z-index: 1000;
  opacity: 0;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  max-width: 90%;
  text-align: center;
  
  &.show {
    transform: translateX(-50%) translateY(0);
    opacity: 1;
  }
  
  &.toast-success { background: #2ecc71; }
  &.toast-error { background: #e74c3c; }
  &.toast-info { background: #3498db; }
}

// Modal
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  opacity: 1 !important;
  visibility: visible !important;
  animation: none !important;
  
  .modal-content {
    background: white;
    border-radius: 16px;
    width: 100%;
    max-width: 400px;
    max-height: 80vh;
    overflow-y: auto;
    opacity: 1 !important;
    visibility: visible !important;
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
  
  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
  }
  
  .close-btn {
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: #999;
    padding: 0;
    line-height: 1;
  }
}

.modal-body {
  padding: 20px;
  font-size: 13px;
  line-height: 1.6;
  color: #666;
  
  h4 {
    color: #2c3e50;
    margin: 0 0 12px 0;
    font-size: 14px;
  }
  
  p {
    margin: 0 0 12px 0;
  }
}
</style>
